import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@app/config/axios.config'
import { Endpoint } from '@app/config/endpoints'
import { useElementGroupBuckets } from './useElementGroupBuckets'
import { useHazardGroupBuckets } from './useHazardGroupBuckets'
import { resolveRegionFilters } from '../utils/riskProfile.utils'
import type { RiskProfileQueryParams } from './useRiskProfileQueryParams'
import type {
  RiskProfileSummaryRequest,
  RiskProfileSummaryResponse,
} from '../types/riskProfileSummary.types'
import { filterElementBuckets } from '../utils/riskProfileBuckets.utils'

export const RISK_PROFILE_SUMMARY_QUERY_KEY = 'risk-profile-summary'

const fetchProfileSummary = async (
  request: RiskProfileSummaryRequest,
): Promise<RiskProfileSummaryResponse> => {
  const { data } = await apiClient.post<RiskProfileSummaryResponse>(
    Endpoint.riskProfileSummary,
    request,
  )
  return data
}

export const useProfileSummaryQuery = (request: RiskProfileSummaryRequest | null) => {
  return useQuery({
    queryKey: [RISK_PROFILE_SUMMARY_QUERY_KEY, request],
    queryFn: () => {
      if (request === null) {
        throw new Error('Profile summary request is required')
      }
      return fetchProfileSummary(request)
    },
    enabled: request !== null,
  })
}

const useProfileSummaryRequest = (
  queryParams: RiskProfileQueryParams | null,
  granularity: 'type' | 'group',
): RiskProfileSummaryRequest | null => {
  const hazardGroupBuckets = useHazardGroupBuckets(queryParams?.hazards ?? [])
  const elementGroupBuckets = useElementGroupBuckets(queryParams?.assets ?? [])

  return useMemo(() => {
    if (queryParams === null) return null

    const regionFilters = resolveRegionFilters(queryParams.areas, queryParams.areaGroup)

    const filteredHazardBuckets =
      queryParams.selectedHazardGroups.length === 0
        ? hazardGroupBuckets
        : hazardGroupBuckets.filter((bucket) =>
            queryParams.selectedHazardGroups.includes(Object.keys(bucket)[0]),
          )

    const filteredHazardIds =
      queryParams.selectedHazardGroups.length === 0
        ? queryParams.hazards
        : filteredHazardBuckets.flatMap((bucket) => Object.values(bucket)[0])

    const filteredElementBuckets = filterElementBuckets(
      elementGroupBuckets,
      queryParams.selectedElementGroups,
    )

    const buckets =
      granularity === 'group'
        ? filteredElementBuckets
        : filteredElementBuckets
            .flatMap((bucket) => Object.values(bucket)[0])
            .map((id) => ({ [queryParams.elementTitles[id] ?? id]: [id] }))

    return {
      group_by: { element_group: buckets },
      hazards: filteredHazardIds,
      ...regionFilters,
    }
  }, [elementGroupBuckets, granularity, hazardGroupBuckets, queryParams])
}

export function useProfileTypeSummaryData(queryParams: RiskProfileQueryParams | null) {
  const request = useProfileSummaryRequest(queryParams, 'type')
  const query = useProfileSummaryQuery(request)
  return { request, query }
}

export function useProfileGroupSummaryData(queryParams: RiskProfileQueryParams | null) {
  const request = useProfileSummaryRequest(queryParams, 'group')
  const query = useProfileSummaryQuery(request)
  return { request, query }
}
